import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Card, CardContent } from "./ui/card";
import { Mail, MapPin, Phone } from "lucide-react";
import { personalInfo } from "../mock";
import { toast } from "../hooks/use-toast";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const form = e.currentTarget;
      const data = new FormData(form);

      const res = await fetch("https://formspree.io/f/mqaroqgn", {
        method: "POST",
        body: data,
        headers: {
          Accept: "application/json",
        },
      });

      if (!res.ok) throw new Error("Formspree submit failed");

      toast({
        title: "Mesaj gönderildi!",
        description: "Mesajınız başarıyla iletildi.",
      });

      setFormData({ name: "", email: "", subject: "", message: "" });
      form.reset();
    } catch (err) {
      console.error(err);
      toast({
        title: "Bir hata oluştu",
        description: "Mesaj gönderilemedi.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-32 bg-gray-50">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
            İletişim
          </h2>
          <p className="text-lg text-gray-600 mb-16 text-center max-w-2xl mx-auto">
            Bir proje fikriniz mi var? Benimle iletişime geçin!
          </p>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info Cards */}
            <div className="space-y-6">
              <Card className="border-2 border-gray-200 hover:border-gray-900 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gray-100 rounded-lg">
                      <Mail size={24} className="text-gray-900" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">
                        Email
                      </h3>
                      <a
                        href={`mailto:${personalInfo.email}`}
                        className="text-gray-600 hover:text-gray-900 transition-colors"
                      >
                        {personalInfo.email}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200 hover:border-gray-900 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gray-100 rounded-lg">
                      <Phone size={24} className="text-gray-900" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">
                        Telefon
                      </h3>
                      <a
                        href={`tel:${personalInfo.phone}`}
                        className="text-gray-600 hover:text-gray-900 transition-colors"
                      >
                        {personalInfo.phone}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200 hover:border-gray-900 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gray-100 rounded-lg">
                      <MapPin size={24} className="text-gray-900" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">
                        Konum
                      </h3>
                      <p className="text-gray-600">{personalInfo.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-2 border-gray-200">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Formspree subject */}
                    <input type="hidden" name="_subject" value={formData.subject} />

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Adınız
                        </label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Adınızı girin"
                          className="border-2 border-gray-300 focus:border-gray-900 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Email
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="email@example.com"
                          className="border-2 border-gray-300 focus:border-gray-900 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Konu
                      </label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        placeholder="Mesaj konusu"
                        className="border-2 border-gray-300 focus:border-gray-900 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Mesaj
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        placeholder="Mesajınızı yazın..."
                        className="border-2 border-gray-300 focus:border-gray-900 transition-colors resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gray-900 hover:bg-gray-800 text-white py-6 text-base font-medium transition-all duration-300 transform hover:scale-105"
                    >
                      {loading ? "Gönderiliyor..." : "Mesaj Gönder"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
